import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Paperclip, Camera, Trash2, Download, FileText, Lock } from "lucide-react";
import { useStore } from "../context/StoreContext";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";

const MAX = 15 * 1024 * 1024;

// Shrink large photos so receipts stay readable but small on the device
export async function shrink(file) {
  if (!file.type.startsWith("image/") || file.size < 600 * 1024) return file;
  try {
    const bmp = await createImageBitmap(file);
    const scale = Math.min(1, 1800 / Math.max(bmp.width, bmp.height));
    const c = document.createElement("canvas");
    c.width = Math.round(bmp.width * scale);
    c.height = Math.round(bmp.height * scale);
    c.getContext("2d").drawImage(bmp, 0, 0, c.width, c.height);
    const blob = await new Promise((r) => c.toBlob(r, "image/jpeg", 0.85));
    return blob ? new File([blob], file.name.replace(/\.\w+$/, "") + ".jpg", { type: "image/jpeg" }) : file;
  } catch {
    return file;
  }
}

function ReceiptItem({ txId, meta }) {
  const { getReceipt, purgeReceipt } = useStore();
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let u;
    getReceipt(meta).then((b) => setUrl(b ? (u = URL.createObjectURL(b)) : "error")).catch(() => setUrl("error"));
    return () => u && URL.revokeObjectURL(u);
  }, [meta.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const isImg = meta.type?.startsWith("image/");
  const remove = async () => {
    if (!window.confirm(`Delete ${meta.name} from this device?`)) return;
    await purgeReceipt(txId, meta.id);
    toast.success("Receipt deleted");
  };
  return (
    <li className="rounded-xl border overflow-hidden bg-card" data-testid={`receipt-item-${meta.id}`}>
      <a href={url && url !== "error" ? url : undefined} target="_blank" rel="noreferrer" className="block h-36 bg-secondary grid place-items-center" data-testid={`receipt-open-${meta.id}`}>
        {url === "error" ? <span className="text-xs text-expense p-2">File isn't on this device</span> : isImg && url ? <img src={url} alt={meta.name} className="h-full w-full object-cover" /> : <FileText className="h-10 w-10 text-muted-foreground" />}
      </a>
      <div className="p-2 flex items-center gap-1">
        <p className="text-xs truncate flex-1" title={meta.name}>{meta.name}</p>
        {url && url !== "error" && <Button variant="ghost" size="icon" className="h-7 w-7" asChild><a href={url} download={meta.name} data-testid={`receipt-download-${meta.id}`}><Download className="h-3.5 w-3.5" /></a></Button>}
        <Button variant="ghost" size="icon" className="h-7 w-7 text-expense" onClick={remove} data-testid={`receipt-delete-${meta.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
      </div>
    </li>
  );
}

function ReceiptDialog({ tx, onClose }) {
  const { addReceipt } = useStore();
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const camRef = useRef(null);
  const add = async (files) => {
    setBusy(true);
    for (const f of [...files]) {
      if (f.size > MAX) { toast.error(`${f.name} is larger than 15 MB`); continue; }
      try {
        await addReceipt(tx.id, await shrink(f));
        toast.success(`Saved ${f.name}`);
      } catch (e) {
        console.error(e);
        toast.error(`Couldn't save ${f.name} — the device may be out of space`);
      }
    }
    setBusy(false);
  };
  const pick = (e) => { e.target.files?.length && add(e.target.files); e.target.value = ""; };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="receipt-dialog">
        <DialogHeader>
          <DialogTitle>Receipts · {tx.description}</DialogTitle>
          <DialogDescription className="flex items-center gap-1.5"><Lock className="h-3.5 w-3.5" /> Stored encrypted on this device only. Included in backups when "Include receipt photos" is ticked in Settings.</DialogDescription>
        </DialogHeader>
        <div className="flex flex-wrap gap-2">
          <Button onClick={() => fileRef.current?.click()} disabled={busy} data-testid="receipt-add-button"><Paperclip className="h-4 w-4 mr-1" /> {busy ? "Saving…" : "Add photo or PDF"}</Button>
          <Button variant="outline" onClick={() => camRef.current?.click()} disabled={busy} data-testid="receipt-camera-button"><Camera className="h-4 w-4 mr-1" /> Take photo</Button>
          <input ref={fileRef} type="file" accept="image/*,application/pdf" multiple className="hidden" onChange={pick} data-testid="receipt-file-input" />
          <input ref={camRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={pick} />
        </div>
        {tx.receipts?.length ? (
          <ul className="grid grid-cols-2 sm:grid-cols-3 gap-3 max-h-[50vh] overflow-y-auto" data-testid="receipt-list">
            {tx.receipts.map((r) => <ReceiptItem key={r.id} txId={tx.id} meta={r} />)}
          </ul>
        ) : <p className="text-sm text-muted-foreground" data-testid="receipt-empty">No receipts yet. Add a photo of the paper receipt or a PDF invoice.</p>}
      </DialogContent>
    </Dialog>
  );
}

export function ReceiptButton({ txId }) {
  const { state } = useStore();
  const [open, setOpen] = useState(false);
  const tx = state.transactions.find((t) => t.id === txId);
  if (!tx) return null;
  const n = tx.receipts?.length || 0;
  return (
    <>
      <Button variant={n ? "secondary" : "ghost"} size="sm" className="h-7 px-2 text-xs" onClick={() => setOpen(true)} data-testid={`receipt-button-${txId}`}>
        <Paperclip className="h-3.5 w-3.5 mr-1" />{n || "Add"}
      </Button>
      {open && <ReceiptDialog tx={tx} onClose={() => setOpen(false)} />}
    </>
  );
}

export function ReceiptThumb({ meta, className = "" }) {
  const { getReceipt } = useStore();
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let u;
    getReceipt(meta).then((b) => setUrl(b ? (u = URL.createObjectURL(b)) : "missing")).catch(() => setUrl("missing"));
    return () => u && URL.revokeObjectURL(u);
  }, [meta.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const box = `bg-secondary grid place-items-center ${className}`;
  if (url === "missing") return <div className={`${box} text-[10px] text-center text-muted-foreground p-1`}>Photo isn't on this device</div>;
  if (!url) return <div className={box} />;
  if (!meta.type?.startsWith("image/")) return <a href={url} target="_blank" rel="noreferrer" className={box}><FileText className="h-8 w-8 text-muted-foreground" /></a>;
  return <a href={url} target="_blank" rel="noreferrer" className="block"><img src={url} alt={meta.name} loading="lazy" className={`object-cover bg-white ${className}`} /></a>;
}
